/**
 * The sidebar's grouping, and the collapsed state that persists with it.
 *
 * WHY GROUPS ARE DATA AND NOT MARKUP
 * ----------------------------------
 * A group renders only when at least one of its items survives the EXISTING
 * per-item permission filter. There is deliberately no permission on a group:
 * a second gate would be a second thing to keep in step with the first, and the
 * first one already knows the answer. A technician who can see nothing under
 * ADMIN does not see the word ADMIN, because the group has nothing to draw.
 */

export interface NavGroup {
  id: string;
  /** null = no heading; the item sits alone at the top. */
  heading: string | null;
  /** Paths, in display order. Resolved against the filtered item list. */
  paths: string[];
}

export const NAV_GROUPS: NavGroup[] = [
  { id: 'top', heading: null, paths: ['/dashboard'] },
  {
    id: 'workspace',
    heading: 'Workspace',
    // /my-bookings right under Spaces (Bug_121): offered to everyone who can
    // book (routeAccess NAV_OFFER). The employee's own menu lists it from
    // config/employeeExperience instead.
    paths: ['/spaces', '/my-bookings', '/hybrid', '/visitors', '/wellbeing'],
  },
  {
    id: 'operations',
    heading: 'Operations',
    paths: ['/helpdesk', '/maintenance', '/assets', '/scan',
            '/maintenance/checklists', '/contractors', '/iot', '/safety',
            // FM Technician C14: the technician's red Report incident
            // (offered to the technician experience only — TECH_ONLY_NAV).
            '/tech/incident'],
  },
  { id: 'real-estate', heading: 'Real estate', paths: ['/tenants', '/tenant-ops', '/leases', '/parking'] },
  { id: 'sustainability', heading: 'Sustainability', paths: ['/energy', '/compliance'] },
  { id: 'intelligence', heading: 'Intelligence',
    paths: ['/analytics', '/marketplace/integrations'] },
  // Settings is deliberately NOT here. It carries no permission, because it is
  // a person's own preferences rather than an administrative screen — so
  // putting it in this group left an ordinary employee looking at an "Admin"
  // heading with one unrelated link under it. It is pinned below the nav in its
  // own strip instead, and this group now hides completely for anyone without
  // admin rights.
  // 🔴 THE SUPPORT INBOX WAS DECLARED IN THE SIDEBAR AND NEVER SHOWN. The
  // sidebar renders only the paths listed here, and '/super-admin/support' was
  // not one of them — so the platform operator's inbox had no link anywhere.
  // sidebarItemsAreGrouped.test.ts now fails on an item no group lists.
  { id: 'admin', heading: 'Admin',
    paths: ['/admin', '/admin/users', '/admin/reception', '/super-admin/organisations',
            '/super-admin/support'] },
  // Bug_117 (owner decision, 28 Oct 2026): public privacy requests that matched
  // no organisation are a platform privacy duty, not user onboarding — their
  // own super-admin group, out of Admin. The route is super-admin only.
  { id: 'platform-privacy', heading: 'Privacy (platform)',
    paths: ['/super-admin/data-requests'] },
];

/** Which group a path belongs to, or null if it is not in the nav at all. */
export function groupOf(path: string): NavGroup | null {
  return NAV_GROUPS.find((g) => g.paths.includes(path)) ?? null;
}

/**
 * The group containing the current location.
 *
 * Matched by longest path first, so /maintenance/checklists resolves to its own
 * entry rather than to /maintenance — the two are in the same group today, but
 * a prefix match that picks the shorter path is the kind of thing that is
 * correct by luck.
 */
/** Pages that live under a nav entry without sharing its path prefix.
 *  🔴 THE SPACE OPTIMISER HIGHLIGHTED NOTHING. It is at /space/optimiser and
 *  its nav entry is /spaces, so neither the item nor its group read as active
 *  (Spaces regression, Bug 08). */
const NAV_PARENT_PREFIXES: [string, string][] = [
  ['/space/', '/spaces'],
];

/** The nav path a location belongs to, for highlighting. */
export function navPathFor(pathname: string): string {
  const hit = NAV_PARENT_PREFIXES.find(([prefix]) => pathname.startsWith(prefix));
  return hit ? hit[1] : pathname;
}

export function activeGroupId(pathname: string): string | null {
  let best: { id: string; len: number } | null = null;
  for (const g of NAV_GROUPS) {
    for (const p of g.paths) {
      if (pathname === p || pathname.startsWith(p + '/')) {
        if (!best || p.length > best.len) best = { id: g.id, len: p.length };
      }
    }
  }
  return best?.id ?? null;
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------
/**
 * WHAT THE APP ACTUALLY HAS, AND WHY THIS IS NOT THAT.
 *
 * The real preference mechanism is PUT /auth/me/preferences, which writes to
 * named columns on the user's row — language, timezone, date_format, theme,
 * density, notifications_enabled, default_building_id. ThemeProvider and
 * DensityProvider both use it, and both treat localStorage as a pre-paint cache
 * rather than as the truth.
 *
 * That endpoint is a CLOSED WHITELIST. There is no free-form blob to put nav
 * state in, and there deliberately is not: four of those fields used to live in
 * organisations.settings under a "user_preferences:{id}" key, which grew a key
 * per user and kept them after the user was deleted. That was removed on
 * purpose, and re-using it here would put it back.
 *
 * So carrying this preference on the account needs one new column and one line
 * in UserPreferencesUpdate — a BACKEND change, which this task excludes. Until
 * then the state lives on the device, written through the single pair of
 * functions below so that swapping in the server field is a change in one place
 * and nowhere else.
 */

//: Exported so tests use the real key rather than a copy of it. A literal
//: repeated in a test is a literal that can drift from the module — which it
//: promptly did: a rebranding pass rewrote the test's copy and not this one, so
//: the test wrote to one key and the code read another.
export const NAV_STORAGE_KEY = 'spaciora-nav-collapsed-groups';

export function readCollapsedGroups(): string[] {
  try {
    const raw = localStorage.getItem(NAV_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Only ids that still exist. A group renamed or removed in a later release
    // must not leave an orphan id that quietly collapses nothing.
    const known = new Set(NAV_GROUPS.map((g) => g.id));
    return Array.isArray(parsed) ? parsed.filter((x) => known.has(x)) : [];
  } catch {
    // Private browsing, a full quota, or a hostile value. A nav that throws on
    // load is worse than a nav that forgets.
    return [];
  }
}

export function writeCollapsedGroups(ids: string[]): void {
  try {
    localStorage.setItem(NAV_STORAGE_KEY, JSON.stringify([...new Set(ids)]));
  } catch {
    /* forgetting is acceptable; failing to render is not */
  }
}

//: DSH-042: whether the whole sidebar is collapsed to icons. It was
//: useState(false) in AppLayout and forgotten on every reload. Kept on the
//: device for the same reason, and through the same kind of pair, as the
//: group state above.
export const SIDEBAR_COLLAPSED_KEY = 'spaciora-sidebar-collapsed';

export function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeSidebarCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    /* forgetting is acceptable; failing to render is not */
  }
}
